import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import type { JobFileEntry } from '@jobos/shared';

import { FileViewerShell } from '@/components/shared/FileViewerShell';
import { Button } from '@/components/ui/button';
import { useImageZoom } from '@/hooks/useImageZoom';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useJobFileUrl } from '@/hooks/useJobFiles';
import { formatDate } from '@/lib/utils';
import { useJobFileDownload } from './useJobFileDownload';

// Photo viewing on the Files tab: the point of the library is that a photo can be
// looked at on the phone that took it without downloading anything first — and
// looked at closely, which is what the zoom is for. A site photo is usually taken
// to record a detail, and the detail is rarely the size of the frame.
//
// It shares `FileViewerShell` with the document viewer, so opening a photo and
// opening a PDF are the same window with the same header — a file is a file, and
// the tab shouldn't feel like two different apps depending on what was tapped.
//
// The set it pages through is decided by the caller — it is the images of what
// the user is currently looking at, filtered and in display order, so paging
// never surfaces a photo the filters had hidden.
//
// On a desktop screen it ends up showing the full-resolution original rather
// than the 2800px display rendition, because that is where the rendition runs
// out: a phone dialog is ~380 CSS px against a 2800px image and can't resolve it
// at any zoom level, while a desktop dialog is ~1024 and the zoom starts
// interpolating partway up. The original arrives behind the photo already on
// screen — see the progressive upgrade below — so nothing about opening a photo
// gets slower, and a phone never pays for bytes it couldn't display.

interface JobFileLightboxProps {
  jobId: string;
  /** Image entries only, in the order they are shown. */
  files: JobFileEntry[];
  /** Index into `files`; out of range closes the viewer. */
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

export function JobFileLightbox({
  jobId,
  files,
  index,
  onIndexChange,
  onClose,
}: JobFileLightboxProps) {
  const file = files[index];
  const open = file != null;
  const hasPrev = open && index > 0;
  const hasNext = open && index < files.length - 1;

  const [failed, setFailed] = useState(false);
  const download = useJobFileDownload(jobId);

  const { data: url, isError } = useJobFileUrl(jobId, file?.id, 'display', { enabled: open });

  // The neighbour's presign is an API round-trip of its own; paying it before
  // the tap means the next photo is already addressable when it arrives. The
  // display rendition only: an original runs to 20MB, and the neighbour is a
  // photo the user may never page to.
  const nextFile = files[index + 1];
  useJobFileUrl(jobId, nextFile?.id, 'display', { enabled: open && nextFile != null });

  // ---- Progressive upgrade to the original -------------------------------
  //
  // Gated on the display rendition having painted, so the presign and the fetch
  // stay off the path between opening a photo and seeing it. `originalKey` is
  // null whenever an upload's best-effort second phase didn't land, and on the
  // receipt and legacy inspection mirrors, which never had an original at all —
  // those simply keep the display rendition.
  const isDesktop = useIsDesktop();
  const [displayPainted, setDisplayPainted] = useState(false);
  const [originalSrc, setOriginalSrc] = useState<string | null>(null);

  const { data: originalUrl } = useJobFileUrl(jobId, file?.id, 'original', {
    enabled: open && isDesktop && displayPainted && file?.originalKey != null,
  });

  // A hidden <img> does the fetching, so the swap below happens against bytes
  // the browser has already decoded — the photo sharpens in place instead of
  // blanking while a 20MB original loads in the frame the user is looking at.
  const preloadSrc = originalUrl && originalUrl !== originalSrc ? originalUrl : null;

  const step = useCallback(
    (delta: number) => {
      const target = index + delta;
      if (target < 0 || target >= files.length) return;
      onIndexChange(target);
    },
    [files.length, index, onIndexChange],
  );

  const { isZoomed, canZoomIn, canZoomOut, zoomIn, zoomOut, reset, imageStyle, surfaceRef, surfaceProps } =
    useImageZoom({ onSwipe: step });

  // A failure, a zoom level and an upgraded rendition all belong to one photo.
  // Paging away from it starts clean, rather than landing on the next photo
  // already magnified — or showing it at the previous photo's URL.
  useEffect(() => {
    setFailed(false);
    setDisplayPainted(false);
    setOriginalSrc(null);
    reset();
  }, [file?.id, reset]);

  // Escape and the close button are Radix's; the arrows are ours.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') step(1);
      else if (event.key === 'ArrowLeft') step(-1);
      else if (event.key === '+' || event.key === '=') zoomIn();
      else if (event.key === '-') zoomOut();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, step, zoomIn, zoomOut]);

  return (
    <FileViewerShell
      open={open}
      onClose={onClose}
      title={file?.fileName ?? ''}
      // Dark, unlike the document viewer's light body: a photo is read by its
      // own colours, and a bright surround is what fights it.
      bodyClassName="bg-gray-900"
      actions={
        file && (
          <>
            {/* Only worth a button once there is something to undo, and it sits
                ahead of the zoom controls so the pair the user is tapping
                repeatedly keeps its place against the Download button. */}
            {isZoomed && (
              <Button variant="outline" size="sm" onClick={reset} className="shrink-0">
                Reset
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              aria-label="Zoom out"
              disabled={!canZoomOut}
              onClick={zoomOut}
              className="h-8 w-8 shrink-0"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label="Zoom in"
              disabled={!canZoomIn}
              onClick={zoomIn}
              className="h-8 w-8 shrink-0"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            {/* Serves the original, not the rendition on screen — the file the
                user uploaded is the one they expect back. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => download(file.id)}
              className="shrink-0"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </>
        )
      }
    >
      {file && (
        <>
          <div
            ref={surfaceRef}
            data-testid="job-file-lightbox-surface"
            {...surfaceProps}
            className={`flex h-full w-full items-center justify-center overflow-hidden pb-9 ${
              isZoomed ? 'cursor-grab active:cursor-grabbing' : ''
            }`}
            // A zoomed photo owns its drags; letting the browser scroll or
            // navigate on the same gesture would fight the pan.
            style={{ touchAction: isZoomed ? 'none' : 'pan-y' }}
          >
            {isError || failed ? (
              <p className="px-6 text-center text-sm text-gray-300">
                Couldn't load this image. It may still be uploading — try again shortly.
              </p>
            ) : url ? (
              <>
                <img
                  src={originalSrc ?? url}
                  alt={file.fileName}
                  onLoad={() => setDisplayPainted(true)}
                  onError={() => setFailed(true)}
                  draggable={false}
                  style={imageStyle}
                  className="max-h-full max-w-full select-none object-contain"
                />
                {/* Display:none still fetches. An original that fails to load is
                    left where it is — the photo on screen is already the one the
                    user asked for, so there is nothing to report. */}
                {preloadSrc && (
                  <img
                    data-testid="job-file-lightbox-original-preload"
                    src={preloadSrc}
                    alt=""
                    aria-hidden
                    onLoad={() => setOriginalSrc(preloadSrc)}
                    className="hidden"
                  />
                )}
              </>
            ) : (
              <Loader2
                data-testid="job-file-lightbox-loading"
                aria-label="Loading photo"
                className="h-8 w-8 animate-spin text-gray-400"
              />
            )}
          </div>

          {/* Edge-anchored and thumb-sized: on a phone these are where the hand
              already is, and they must not sit under the caption bar. Hidden
              while zoomed — at that point the edges of the frame are where the
              user is dragging, not where they want to change photo. */}
          {!isZoomed && (
            <>
              <button
                type="button"
                aria-label="Previous photo"
                disabled={!hasPrev}
                onClick={() => step(-1)}
                className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white disabled:opacity-25"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                aria-label="Next photo"
                disabled={!hasNext}
                onClick={() => step(1)}
                className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white disabled:opacity-25"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* The filename lives in the header now, as it does for a document; what
              is left is what the header can't say — who, when, and where in the set. */}
          <div
            data-testid="job-file-lightbox-caption"
            className="absolute inset-x-0 bottom-0 truncate bg-black/70 px-4 py-2 text-xs text-gray-300"
          >
            {file.uploadedByName ?? '—'} ·{' '}
            <span data-testid="job-file-lightbox-date">{formatDate(file.createdAt)}</span> ·{' '}
            {index + 1} of {files.length}
          </div>
        </>
      )}
    </FileViewerShell>
  );
}
